/**
 * /brands — the "ready-to-launch" brand showcase. Every listing here
 * has a logo, a curator's story, and is priced + configured to hand
 * over as a full brand identity, not just a bare domain name.
 *
 * Design language sits between /premium (yellow, curated) and the
 * Projects portfolio (editorial, portfolio-y). Logo-first cards,
 * generous whitespace, category chips for drill-down.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";
import Seo from "@/components/Seo";

export default function BrandsPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/listings/brands-ready", { params: { limit: 100 } }),
      api.get("/listings/categories"),
    ])
      .then(([b, c]) => {
        setItems(b.data?.items || []);
        setCategories(c.data?.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(
    () => (activeCategory ? items.filter((d) => d.category === activeCategory) : items),
    [items, activeCategory],
  );

  const activeCategories = useMemo(() => {
    const set = new Set(items.map((d) => d.category).filter(Boolean));
    return categories.filter((c) => set.has(c.key));
  }, [categories, items]);

  return (
    <>
      <Seo
        title="Brands ready-to-go · CasperDomains"
        description="Domains that arrive pre-branded. Logo, story, and full identity — plug in and launch tomorrow."
      />
      <SiteHeader />
      <main className="cd-container" style={{ paddingTop: 80, paddingBottom: 80, minHeight: "70vh" }}>
        <div style={{ marginBottom: 40 }}>
          <span className="cd-eyebrow" style={{ color: "var(--cd-blue-500, #2563eb)" }}>
            Ready-to-launch · pre-branded
          </span>
          <h1 className="cd-display" style={{ marginTop: 12, fontSize: "clamp(40px, 5vw, 64px)" }} data-testid="brands-page-title">
            Brands ready-to-go.
          </h1>
          <p className="cd-lead" style={{ maxWidth: 720, marginTop: 20 }}>
            Each name below arrives dressed. Logo designed, story written, forwarding wired.
            Buy today, launch tomorrow — no branding sprint required.
          </p>
        </div>

        {activeCategories.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 40 }} data-testid="brands-category-chips">
            <button
              onClick={() => setActiveCategory("")}
              className="cd-btn cd-btn--sm"
              style={{
                background: activeCategory === "" ? "var(--cd-navy-950)" : "white",
                color: activeCategory === "" ? "white" : "var(--cd-navy-950)",
                border: "1px solid var(--cd-gray-200)",
                fontSize: 11, padding: "6px 14px", borderRadius: 999,
              }}
              data-testid="brands-chip-all"
            >
              All ({items.length})
            </button>
            {activeCategories.map((c) => {
              const n = items.filter((d) => d.category === c.key).length;
              return (
                <button
                  key={c.key}
                  onClick={() => setActiveCategory(c.key === activeCategory ? "" : c.key)}
                  className="cd-btn cd-btn--sm"
                  style={{
                    background: activeCategory === c.key ? "var(--cd-navy-950)" : "white",
                    color: activeCategory === c.key ? "white" : "var(--cd-navy-950)",
                    border: "1px solid var(--cd-gray-200)",
                    fontSize: 11, padding: "6px 14px", borderRadius: 999,
                  }}
                  data-testid={`brands-chip-${c.key}`}
                >
                  {c.key} <span style={{ opacity: 0.55, marginLeft: 4 }}>({n})</span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 80, color: "var(--cd-gray-500)" }}>Loading brands…</div>
        ) : visible.length === 0 ? (
          <div
            style={{ textAlign: "center", padding: 80, background: "var(--cd-gray-50)", borderRadius: 18, color: "var(--cd-gray-500)" }}
            data-testid="brands-empty"
          >
            {items.length === 0
              ? "No ready-to-launch brands yet. Flag a listing as “Brand-ready” in the admin to feature it here."
              : `No brands in ${activeCategory} yet.`}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 24,
            }}
            data-testid="brands-grid"
          >
            {visible.map((d) => (
              <BrandCard key={d.domain} d={d} />
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
      <BullseyeEmbed page="brands" />
    </>
  );
}

function BrandCard({ d }) {
  const price = (d.currency || "EUR") === "EUR" ? `€${(d.price || 0).toLocaleString()}` : `$${(d.price || 0).toLocaleString()}`;
  return (
    <Link
      to={`/l/${d.domain}`}
      data-testid={`brand-card-${d.domain}`}
      style={{
        display: "block",
        border: "1px solid var(--cd-gray-200)",
        borderRadius: 16,
        background: "white",
        textDecoration: "none",
        color: "inherit",
        overflow: "hidden",
        transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 16px 40px rgba(3,20,47,0.08)";
        e.currentTarget.style.borderColor = "var(--cd-navy-950)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "var(--cd-gray-200)";
      }}
    >
      {/* Logo band — the whole point of /brands. When no logo is set,
          render an editorial slab with the wordmark so cards stay uniform. */}
      <div
        style={{
          height: 180,
          background: d.domain_logo_url
            ? `#fafafa url("${d.domain_logo_url}") center / contain no-repeat`
            : "linear-gradient(135deg, var(--cd-navy-950) 0%, #0b274d 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid var(--cd-gray-200)",
        }}
      >
        {!d.domain_logo_url && (
          <div style={{
            color: "white", fontFamily: "var(--cd-font-display)",
            fontSize: 26, fontWeight: 700, letterSpacing: "-.01em",
            textAlign: "center", padding: "0 24px", wordBreak: "break-word",
          }}>
            {d.domain}
          </div>
        )}
      </div>
      <div style={{ padding: "22px 22px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          {d.is_premium && (
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".1em", color: "#92400e", background: "#fef3c7", padding: "3px 7px", borderRadius: 999, textTransform: "uppercase" }}>
              ★ Premium
            </span>
          )}
          {d.category && (
            <span style={{ fontSize: 10, fontWeight: 600, color: "var(--cd-gray-500)", letterSpacing: ".05em", textTransform: "uppercase" }}>
              {d.category}
            </span>
          )}
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "var(--cd-navy-950)", lineHeight: 1.2, marginBottom: 8, wordBreak: "break-word" }}>
          {d.domain}
        </div>
        {(d.premium_story || d.ai_copy?.tagline) && (
          <div style={{ fontSize: 13, color: "var(--cd-gray-600)", marginBottom: 16, lineHeight: 1.5 }}>
            {d.premium_story || d.ai_copy.tagline}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--cd-navy-950)" }}>
            {price}
          </div>
          <div style={{ fontSize: 11, color: "var(--cd-blue-500, #2563eb)", fontWeight: 600 }}>
            View brand →
          </div>
        </div>
      </div>
    </Link>
  );
}
